import type {
  CreateModelRequest,
  CreatePricingRequest,
  CreateProtocolRequest,
  DeleteModelResponse,
  DeleteProtocolResponse,
  DeprecateModelRequest,
  ModelPricingRecord,
  ModelProtocol,
  ModelProtocolRecord,
  ModelRecord,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  addProtocol,
  createModel,
  createPricing,
  deleteModel,
  deleteProtocol,
  delistModel,
  deprecateModel,
  getModel,
  listModel,
  listModels,
  listPricing,
  listProtocols,
  undeprecateModel,
  updateModel,
} from './models.ts';

/**
 * React Query wrappers for the models API (Wave 5C — same shape as
 * `useUpstreams.ts`).
 *
 * Query key conventions:
 *   - `['models']`                                        — list query
 *   - `['models', vendor, model]`                         — single model
 *   - `['models', vendor, model, 'pricing']`              — pricing history
 *   - `['models', vendor, model, 'protocols']`            — registered protocols
 *
 * Mutation invalidation map:
 *   - createModel / deleteModel        → invalidate `['models']`. Delete
 *     also marks the specific entry stale so any open detail view re-fetches.
 *   - updateModel                      → invalidate the specific
 *     `['models', vendor, model]` key plus the list (display name etc.
 *     show in the table).
 *   - createPricing                    → invalidate `['models', vendor, model,
 *     'pricing']` (history list) and the list (current input/output rates
 *     are surfaced inline in the table).
 *   - addProtocol / deleteProtocol     → invalidate `['models', vendor,
 *     model, 'protocols']`. The list view also surfaces protocol chips so
 *     the table query is invalidated too.
 */

export const MODELS_QUERY_KEY = ['models'] as const;

export function modelQueryKey(
  vendor: string,
  model: string,
): readonly ['models', string, string] {
  return ['models', vendor, model] as const;
}

export function modelPricingQueryKey(
  vendor: string,
  model: string,
): readonly ['models', string, string, 'pricing'] {
  return ['models', vendor, model, 'pricing'] as const;
}

export function modelProtocolsQueryKey(
  vendor: string,
  model: string,
): readonly ['models', string, string, 'protocols'] {
  return ['models', vendor, model, 'protocols'] as const;
}

export function useModelsList(): UseQueryResult<ModelRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: MODELS_QUERY_KEY,
    queryFn: ({ signal }) => listModels(fetchImpl, signal),
  });
}

function pairOrThrow(
  vendor: string | undefined,
  model: string | undefined,
  hookName: string,
): { vendor: string; model: string } {
  if (vendor === undefined || vendor.length === 0 || model === undefined || model.length === 0) {
    throw new Error(`${hookName}: vendor + model are required`);
  }
  return { vendor, model };
}

export function useModel(
  vendor: string | undefined,
  model: string | undefined,
): UseQueryResult<ModelRecord> {
  const { fetchImpl } = useSession();
  const enabled =
    vendor !== undefined && vendor.length > 0 && model !== undefined && model.length > 0;
  return useQuery({
    queryKey: modelQueryKey(vendor ?? '', model ?? ''),
    queryFn: ({ signal }) => {
      const pair = pairOrThrow(vendor, model, 'useModel');
      return getModel(pair.vendor, pair.model, fetchImpl, signal);
    },
    enabled,
  });
}

export function useModelPricing(
  vendor: string | undefined,
  model: string | undefined,
): UseQueryResult<ModelPricingRecord[]> {
  const { fetchImpl } = useSession();
  const enabled =
    vendor !== undefined && vendor.length > 0 && model !== undefined && model.length > 0;
  return useQuery({
    queryKey: modelPricingQueryKey(vendor ?? '', model ?? ''),
    queryFn: ({ signal }) => {
      const pair = pairOrThrow(vendor, model, 'useModelPricing');
      return listPricing(pair.vendor, pair.model, fetchImpl, signal);
    },
    enabled,
  });
}

export function useModelProtocols(
  vendor: string | undefined,
  model: string | undefined,
): UseQueryResult<ModelProtocolRecord[]> {
  const { fetchImpl } = useSession();
  const enabled =
    vendor !== undefined && vendor.length > 0 && model !== undefined && model.length > 0;
  return useQuery({
    queryKey: modelProtocolsQueryKey(vendor ?? '', model ?? ''),
    queryFn: ({ signal }) => {
      const pair = pairOrThrow(vendor, model, 'useModelProtocols');
      return listProtocols(pair.vendor, pair.model, fetchImpl, signal);
    },
    enabled,
  });
}

export function useCreateModel(): UseMutationResult<
  ModelRecord,
  Error,
  CreateModelRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createModel(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
    },
  });
}

export interface UpdateModelVars {
  readonly vendor: string;
  readonly model: string;
  readonly body: Parameters<typeof updateModel>[2];
}

export function useUpdateModel(): UseMutationResult<ModelRecord, Error, UpdateModelVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model, body }) => updateModel(vendor, model, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: modelQueryKey(vars.vendor, vars.model) });
    },
  });
}

export interface DeleteModelVars {
  readonly vendor: string;
  readonly model: string;
}

export function useDeleteModel(): UseMutationResult<DeleteModelResponse, Error, DeleteModelVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model }) => deleteModel(vendor, model, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: modelQueryKey(vars.vendor, vars.model) });
    },
  });
}

export interface UpdateShelfVars {
  readonly vendor: string;
  readonly model: string;
  readonly reason?: string | null;
}

export function useListModel(): UseMutationResult<ModelRecord, Error, UpdateShelfVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model, reason }) => listModel(vendor, model, { reason }, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: modelQueryKey(vars.vendor, vars.model) });
    },
  });
}

export function useDelistModel(): UseMutationResult<ModelRecord, Error, UpdateShelfVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model, reason }) => delistModel(vendor, model, { reason }, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: modelQueryKey(vars.vendor, vars.model) });
    },
  });
}

export interface DeprecateModelVars {
  readonly vendor: string;
  readonly model: string;
  readonly body: DeprecateModelRequest;
}

export function useDeprecateModel(): UseMutationResult<ModelRecord, Error, DeprecateModelVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model, body }) => deprecateModel(vendor, model, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: modelQueryKey(vars.vendor, vars.model) });
    },
  });
}

export interface UndeprecateModelVars {
  readonly vendor: string;
  readonly model: string;
}

export function useUndeprecateModel(): UseMutationResult<ModelRecord, Error, UndeprecateModelVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model }) => undeprecateModel(vendor, model, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: modelQueryKey(vars.vendor, vars.model) });
    },
  });
}

export interface CreatePricingVars {
  readonly vendor: string;
  readonly model: string;
  readonly body: CreatePricingRequest;
}

export function useCreatePricing(): UseMutationResult<
  ModelPricingRecord,
  Error,
  CreatePricingVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model, body }) => createPricing(vendor, model, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: modelPricingQueryKey(vars.vendor, vars.model) });
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
    },
  });
}

export interface AddProtocolVars {
  readonly vendor: string;
  readonly model: string;
  readonly body: CreateProtocolRequest;
}

export function useAddProtocol(): UseMutationResult<
  ModelProtocolRecord,
  Error,
  AddProtocolVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model, body }) => addProtocol(vendor, model, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: modelProtocolsQueryKey(vars.vendor, vars.model) });
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
    },
  });
}

export interface DeleteProtocolVars {
  readonly vendor: string;
  readonly model: string;
  readonly protocol: ModelProtocol;
}

export function useDeleteProtocol(): UseMutationResult<
  DeleteProtocolResponse,
  Error,
  DeleteProtocolVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendor, model, protocol }) =>
      deleteProtocol(vendor, model, protocol, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: modelProtocolsQueryKey(vars.vendor, vars.model) });
      void qc.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
    },
  });
}
